import { Trans } from '@lingui/macro';
import React, { FunctionComponent } from 'react';

import ClipboardCopy from '@components/common/clipboard_copy/main';
import { PrimaryButton } from '@components/library/button';

interface CDNLinkProps {
  label: string;
  link: string;
  description?: string;
  disabled?: boolean;
  styleClass?: string;
}

/**
 * Determines the link type based on the URL structure, format parameter, and label
 * @param link - The CDN link URL
 * @param label - The label text for the link
 * @returns The detected link type string
 */
const getLinkType = (link: string, label: string): string => {
  // Check for special link types first
  if (link.includes('videogif.bfldr.com')) {
    return 'Video GIF CDN';
  }
  if (link.includes('.m3u8')) {
    return 'HLS Stream';
  }
  if (link.includes('iframe')) {
    return 'Video Embed Code';
  }
  if (BFG?.smartCDNFrameURL && link.includes(BFG.smartCDNFrameURL)) {
    return 'Frame CDN';
  }

  try {
    const linkUrl = new URL(link);
    const formatParam = linkUrl.searchParams.get('format')?.toLowerCase();
    const pathname = linkUrl.pathname;

    // Extract extension from pathname (before ? if present)
    const extensionMatch = pathname.match(/\.([^.?#]+)(?:\?|$|#)/);
    const extension = extensionMatch?.[1]?.toLowerCase();

    if (pathname.includes('/at/')) {
      // Attachment links contain /at/ in the path
      // Original File CDN: no format param OR format matches the extension (no conversion)
      // If format param exists and differs from extension, it's a converted Attachment CDN
      if (!formatParam) {
        return 'Original File CDN';
      }
      if (extension && formatParam === extension) {
        return 'Original File CDN';
      }
      return 'Attachment CDN';
    }

    if (pathname.includes('/as/')) {
      // Asset links contain /as/ in the path
      return 'Asset CDN';
    }
  } catch (e) {
    // URL parsing failed, will fall through to label-based detection
  }

  // Fallback to label-based detection
  if (label.includes('Attachment CDN Link')) {
    return label.includes('Original') ? 'Original File CDN' : 'Attachment CDN';
  }
  if (label.includes('Asset CDN Link')) {
    return 'Asset CDN';
  }
  if (label.includes('Video GIF')) {
    return 'Video GIF CDN';
  }
  if (label.includes('HLS')) {
    return 'HLS Stream';
  }
  if (label.includes('Frame')) {
    return 'Frame CDN';
  }
  if (label.includes('Original')) {
    return 'Original File CDN';
  }

  return 'CDN';
};

const CDNLink: FunctionComponent<CDNLinkProps> = ({
  description,
  disabled,
  label,
  link,
  styleClass
}) => (
  <div className={`cdn-builder ${styleClass}`}>
    <span className="cdn-builder__title bf-label bf-label--primary">
      {label}
    </span>
    <div className="embed-link-container">
      <pre
        className="embed-link"
      >
        <code
          className={`${disabled ? 'disabled-code' : ''}`}
        >
          {link}
        </code>
      </pre>
      <PrimaryButton
        disabled={disabled}
      >
        <ClipboardCopy
          check
          showFeedbackAsTooltip={false}
          textToCopy={link}
          onCopy={() => {
            // Track Copy CDN Link with Amplitude
            if (window.BFG?.amplitude?.enabled && window.amplitudeService) {
              const assetId = window.BFG?.currentAsset?.id || '';
              const linkType = getLinkType(link, label);

            window.amplitudeService.trackEvent('CopyCDNLink-ButtonClicked', {
              elementName: 'CopyCdnLink',
              productArea: 'AssetModal',
              featureArea: 'EmbedTab',
              'key properties': {
                assetId: assetId,
                linkType: linkType
              }
            });
            }
          }}
        >
          <Trans>Copy link</Trans>
        </ClipboardCopy>
      </PrimaryButton>
    </div>
    <p className="embed-link-container__description">
      {description}
    </p>
  </div>
);

export default CDNLink;
